import { useEffect, useRef, useState } from "react";
import { Send, Sparkles, Loader2 } from "lucide-react";
import { streamChat, fetchHistory, getOrCreateSessionId } from "@/lib/chatApi";

/**
 * Reusable streaming chat panel for RevGrow.
 * Props:
 *  - mode: 'concierge' | 'qualifier' | 'article'
 *  - sessionKey: unique key that scopes the persisted session id
 *  - title, subtitle, placeholder, suggestions[]
 *  - article: optional {title, tag, excerpt, body} — required when mode='article'
 *  - initialMessage: optional assistant greeting shown before any user turn
 *  - variant: 'panel' | 'inline'
 *  - onLeadReady: optional callback fired after 3+ turns of qualifier chat (for CTA states)
 */
export default function ChatPanel({
  mode = "concierge",
  sessionKey = "concierge",
  title = "Ask RevGrow AI",
  subtitle = "Powered by Gemini 2.5 Flash",
  placeholder = "Ask about RevGrow, our framework, or your pipeline...",
  suggestions = [],
  article = null,
  initialMessage = null,
  variant = "panel",
  onLeadReady,
  className = "",
}) {
  const [sessionId] = useState(() => getOrCreateSessionId(sessionKey));
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);
  const listRef = useRef(null);

  // Load prior history
  useEffect(() => {
    let alive = true;
    (async () => {
      const prior = await fetchHistory(sessionId);
      if (!alive) return;
      const filtered = prior.filter((m) => m.role === "user" || m.role === "assistant");
      if (filtered.length === 0 && initialMessage) {
        setMessages([{ role: "assistant", content: initialMessage }]);
      } else {
        setMessages(filtered.map((m) => ({ role: m.role, content: m.content })));
      }
    })();
    return () => {
      alive = false;
      if (abortRef.current) abortRef.current.abort();
    };
  }, [sessionId]);

  // Auto-scroll
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, streaming]);

  // Lead qualifier readiness signal
  useEffect(() => {
    if (mode !== "qualifier" || !onLeadReady) return;
    const userTurns = messages.filter((m) => m.role === "user").length;
    if (userTurns >= 3) onLeadReady(messages);
  }, [messages, mode, onLeadReady]);

  const send = async (text) => {
    const value = (text ?? input).trim();
    if (!value || streaming) return;
    setError(null);
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: value }, { role: "assistant", content: "" }]);
    setStreaming(true);

    const controller = new AbortController();
    abortRef.current = controller;

    await streamChat({
      sessionId,
      message: value,
      mode,
      article,
      signal: controller.signal,
      onDelta: (delta) => {
        setMessages((prev) => {
          const next = [...prev];
          const last = next[next.length - 1];
          if (last && last.role === "assistant") {
            next[next.length - 1] = { ...last, content: last.content + delta };
          }
          return next;
        });
      },
      onError: (err) => setError(err),
      onDone: () => setStreaming(false),
    });
    setStreaming(false);
  };

  const stop = () => {
    if (abortRef.current) abortRef.current.abort();
    setStreaming(false);
  };

  const containerClass =
    variant === "inline"
      ? `flex flex-col rounded-2xl border border-white/[0.08] bg-[#0A121A] overflow-hidden ${className}`
      : `flex flex-col h-full bg-[#0A121A] ${className}`;

  return (
    <div data-testid={`chat-panel-${mode}`} className={containerClass}>
      {/* Header */}
      <div className="flex items-center gap-3 px-4 md:px-5 py-3 md:py-4 border-b border-white/5">
        <div className="h-8 w-8 rounded-lg bg-teal/15 border border-teal/30 grid place-items-center text-teal">
          <Sparkles size={14} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-semibold text-sm leading-tight">{title}</div>
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground mt-0.5">
            {subtitle}
          </div>
        </div>
        {streaming && (
          <button
            data-testid={`chat-stop-${mode}`}
            onClick={stop}
            className="text-[11px] font-mono uppercase tracking-[0.18em] text-muted-foreground hover:text-teal"
          >
            Stop
          </button>
        )}
      </div>

      {/* Messages */}
      <div
        ref={listRef}
        data-testid={`chat-messages-${mode}`}
        className={variant === "inline" ? "px-4 md:px-5 py-4 space-y-3 max-h-[380px] overflow-auto no-scrollbar" : "flex-1 px-4 md:px-5 py-4 space-y-3 overflow-auto no-scrollbar"}
      >
        {messages.length === 0 && !streaming && (
          <div className="text-xs text-muted-foreground text-center py-8">
            Start a conversation — try one of the prompts below.
          </div>
        )}

        {messages.map((m, i) => (
          <div
            key={i}
            className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
          >
            <div
              className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                m.role === "user"
                  ? "bg-teal/15 border border-teal/25 text-foreground"
                  : "bg-white/[0.03] border border-white/[0.06] text-foreground/90"
              }`}
            >
              {m.content || (streaming && i === messages.length - 1 ? (
                <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                  <Loader2 size={12} className="animate-spin" /> Thinking...
                </span>
              ) : "")}
            </div>
          </div>
        ))}

        {error && (
          <div className="text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
            {error}
          </div>
        )}
      </div>

      {/* Suggestions */}
      {messages.filter((m) => m.role === "user").length === 0 && suggestions.length > 0 && (
        <div className="px-4 md:px-5 pb-3 flex flex-wrap gap-2">
          {suggestions.map((s) => (
            <button
              key={s}
              data-testid={`chat-suggestion-${mode}`}
              onClick={() => send(s)}
              disabled={streaming}
              className="text-[11px] px-3 py-1.5 rounded-full border border-white/10 hover:border-teal/40 hover:text-teal transition disabled:opacity-50"
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {/* Composer */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          send();
        }}
        className="border-t border-white/5 p-3 md:p-4 flex items-end gap-2"
      >
        <textarea
          data-testid={`chat-input-${mode}`}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          placeholder={placeholder}
          rows={1}
          className="flex-1 resize-none rounded-xl bg-black/40 border border-white/10 px-3 py-2.5 text-sm outline-none focus:border-teal/40 max-h-24"
        />
        <button
          type="submit"
          data-testid={`chat-send-${mode}`}
          disabled={streaming || !input.trim()}
          className="h-10 w-10 shrink-0 rounded-xl bg-teal text-[#050A0F] grid place-items-center hover:opacity-90 transition disabled:opacity-50"
          aria-label="Send"
        >
          {streaming ? <Loader2 size={16} className="animate-spin" /> : <Send size={15} />}
        </button>
      </form>
    </div>
  );
}
